import {useTranslation} from "../../../../__snowpack__/link/packages/old-i18n/dist/i18n.esm.js";
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {execFn, setReverseDotNotation} from "../../../common/generic-react.functions.js";
import Button from "../button/button.component.js";
import {useFormRefs} from "./form.hooks.js";
import {FormActionsContainer, FormContentContainer, FormFieldsetContainer} from "./form.styles.js";
function MainForm({
  children,
  actionButtons = true,
  validation = "onsubmit",
  onSubmit,
  onCancel,
  onValidationError,
  isDisabled
}, ref) {
  const {
    t
  } = useTranslation();
  if (validation === true) {
    validation = "onsubmit";
  }
  const [isDisable, setIsDisabled] = useState(isDisabled);
  const [isValid, setIsValid] = useState(!validation);
  const {
    refChildren,
    refs
  } = useFormRefs(children);
  const formRef = useRef(null);
  useEffect(() => {
    setIsValid(!validation);
    setIsDisabled(isDisabled);
  }, [isDisabled, validation]);
  const getAllElements = useCallback(() => {
    const arrayRefsParsed = [];
    refs.forEach((r) => {
      if (r.current && "getRefs" in r.current) {
        const getRefs = (r.current.getRefs() || []).filter(Boolean);
        arrayRefsParsed.push(...getRefs);
      } else {
        arrayRefsParsed.push(r);
      }
    });
    return arrayRefsParsed;
  }, [refs]);
  const validateForm = useCallback((showErrors = true) => {
    let valid = isValid;
    const invalidElements = [];
    const r = getAllElements();
    if (r?.length) {
      valid = true;
      r.forEach(({
        current
      }) => {
        valid = current ? !!current?.valid(showErrors) && valid : valid;
        if (!valid && current?.el) {
          invalidElements.push(current.el);
        }
      });
    }
    setIsValid(valid);
    return [valid, invalidElements];
  }, [getAllElements, isValid]);
  const getAllValues = useCallback(() => {
    const elements = getAllElements();
    let result = {};
    if (elements) {
      result = elements.map((el) => el.current?.el ? {
        name: el.current.el.name,
        value: el.current.value
      } : void 0).filter(Boolean).reduce((a, x) => setReverseDotNotation({
        name: x?.name,
        value: x?.value
      }, a), {});
    }
    return result;
  }, [getAllElements]);
  useImperativeHandle(ref, () => {
    return {
      el: formRef.current,
      validateForm,
      getAllElements,
      getAllValues
    };
  }, [getAllElements, getAllValues, validateForm]);
  const _onSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validation || validation === "onchange" && isValid) {
      execFn(onSubmit);
      return true;
    }
    const [valid, invalidElements] = validateForm();
    if (valid) {
      execFn(onSubmit);
      return true;
    } else if (invalidElements.length) {
      invalidElements[0].scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "center"
      });
    }
    execFn(onValidationError);
    return false;
  }, [isValid, onSubmit, onValidationError, validateForm, validation]);
  const submitButtons = useMemo(() => /* @__PURE__ */ React.createElement(FormActionsContainer, null, /* @__PURE__ */ React.createElement(Button, {
    size: "lg",
    color: "var(--tertiaryColor)",
    onClick: () => execFn(onCancel)
  }, t("actions.cancel")), isDisable && /* @__PURE__ */ React.createElement(Button, {
    disabled: isDisable,
    size: "lg",
    color: "var(--tertiaryColor)",
    type: "submit"
  }, t("actions.submit")), !isDisable && /* @__PURE__ */ React.createElement(Button, {
    size: "lg",
    color: "var(--secondaryColor)",
    type: "submit"
  }, t("actions.submit"))), [onCancel, t]);
  return /* @__PURE__ */ React.createElement("form", {
    ref: formRef,
    onSubmit: _onSubmit,
    noValidate: true
  }, /* @__PURE__ */ React.createElement(FormFieldsetContainer, null, /* @__PURE__ */ React.createElement(FormContentContainer, null, refChildren)), actionButtons && submitButtons);
}
const Form = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainForm));
Form.displayName = "Form";
export default Form;
